/// @ts-nocheck 
/**
 * Autocomplete Web Component
 * 
 * A reusable autocomplete component that fetches and displays search results
 * as the user types in an input field.
 * 
 * @example Basic usage
 * <auto-complete search-url="/search?q={{value}}" search-method="GET" debounce="300">
 *   <input type="text" placeholder="Search...">
 *   <template data-source="results">
 *     <div class="autocomplete-option" data-value="{{id}}">
 *       <strong>{{title}}</strong>
 *       <p>{{description}}</p>
 *     </div>
 *   </template>
 * </auto-complete>
 * 
 * @example With automatic hidden input (for form submission)
 * <auto-complete name="product_id" search-url="/search?q={{value}}">
 *   <input type="text" placeholder="Search products...">
 *   <template data-source="results">
 *     <div class="autocomplete-option" data-value="{{id}}">
 *       <strong>{{title}}</strong>
 *     </div>
 *   </template>
 * </auto-complete>
 * <!-- Automatically creates: <input type="hidden" name="product_id" value="selected_id"> -->
 * 
 * @example With required validation
 * <auto-complete name="product_id" required search-url="/search?q={{value}}">
 *   <input type="text" placeholder="Search products...">
 *   <template data-source="results">
 *     <div class="autocomplete-option" data-value="{{id}}">{{title}}</div>
 *   </template>
 * </auto-complete>
 * <!-- Creates: <input type="hidden" name="product_id" required> -->
 */
class Autocompletion extends HTMLElement {
  constructor() {
    super();
    
    /** @type {HTMLInputElement|null} */
    this.input = null;
    
    /** @type {HTMLInputElement|null} */
    this.hiddenInput = null;
    
    /** @type {HTMLTemplateElement|null} */
    this.template = null;
    
    /** @type {HTMLElement|null} */
    this.resultPanel = null;
    
    /** @type {number|null} */
    this.debounceTimer = null;
    
    /** @type {AbortController|null} */
    this.abortController = null;
    
    /** @type {string} */
    this.cachedTemplate = '';
    
    /** @type {string} */
    this.dataSourcePath = '';
  }

  connectedCallback() {
    this.#initialize();
  }

  disconnectedCallback() {
    this.#cleanup();
  }


  #initialize() {
    // Find and cache the input element
    this.input = this.querySelector('input[type="text"], input[type="search"], input:not([type="hidden"])');
    if (!this.input) {
      console.error('Autocompletion: Input element is required');
      return;
    }

    // Create hidden input if component has name attribute
    this.#createHiddenInput();

    // Find and cache the template
    const templateElement = this.querySelector('template');
    if (templateElement) {
      this.cachedTemplate = templateElement.innerHTML;
      this.dataSourcePath = templateElement.dataset.source || '';
      templateElement.remove();
    }

    // Create result panel
    this.#createResultPanel();

    // Attach event listeners
    this.#attachEventListeners();
  }


  #createHiddenInput() {
    const name = this.getAttribute('name');
    if (!name) return;

    // Check if hidden input already exists
    const existingHidden = this.querySelector(`input[type="hidden"][name="${name}"]`);
    if (existingHidden) {
      this.hiddenInput = existingHidden;
      
      // Apply required attribute if present
      if (this.hasAttribute('required')) {
        this.hiddenInput.required = true;
      }
      return;
    }

    // Create new hidden input
    this.hiddenInput = document.createElement('input');
    this.hiddenInput.type = 'hidden';
    this.hiddenInput.name = name;
    this.hiddenInput.value = '';
    
    // Add required attribute if present on component
    if (this.hasAttribute('required')) {
      this.hiddenInput.required = true;
    }
    
    // Insert hidden input after the visible input
    if (this.input && this.input.parentNode) {
      this.input.parentNode.insertBefore(this.hiddenInput, this.input.nextSibling);
    } else {
      this.appendChild(this.hiddenInput);
    }

    // Remove name attribute from visible input to avoid conflict
    if (this.input && this.input.hasAttribute('name')) {
      this.input.setAttribute('data-original-name', this.input.getAttribute('name'));
      this.input.removeAttribute('name');
    }
  }

  #createResultPanel() {
    this.resultPanel = document.createElement('div');
    this.resultPanel.className = 'autocomplete-panel';
    this.resultPanel.hidden = true;
    this.appendChild(this.resultPanel);
  }


  #attachEventListeners() {
    if (!this.input) return;

    // Input event with debouncing
    this.input.addEventListener('input', this.#handleInput.bind(this));

    // Focus event to show results if available
    this.input.addEventListener('focus', this.#handleFocus.bind(this));

    // Blur event to hide results
    this.input.addEventListener('blur', this.#handleBlur.bind(this));

    // Keyboard navigation
    this.input.addEventListener('keydown', this.#handleKeydown.bind(this));
  }

  /**
   * Handle input changes
   * @param {Event} event
   *
   */
  #handleInput(event) {
    const value = event.target.value.trim();

    // Clear hidden input when user modifies the visible input
    if (this.hiddenInput) {
      this.hiddenInput.value = '';
    }

    // Clear previous debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Cancel previous fetch request
    this.abortController?.abort();

    // If input is empty, hide panel
    if (!value) {
      this.#hidePanel();
      return;
    }

    // Debounce the search
    const debounceTime = parseInt(this.getAttribute('debounce') || '300', 10);
    this.debounceTimer = setTimeout(() => {
      this.#performSearch(value);
    }, debounceTime);
  }

  /**
   * Handle input focus
   *
   */
  #handleFocus() {
    if (this.resultPanel && this.resultPanel.children.length > 0) {
      this.#showPanel();
    }
  }

  /**
   * Handle input blur
   *
   */
  #handleBlur() {
    // Delay hiding to allow click events on results
    setTimeout(() => {
      this.#hidePanel();
    }, 200);
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event
   *
   */
  #handleKeydown(event) {
    if (!this.resultPanel || this.resultPanel.hidden) return;

    const options = Array.from(this.resultPanel.querySelectorAll('.autocomplete-option'));
    const currentIndex = options.findIndex(opt => opt.classList.contains('autocomplete-option--active'));

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#navigateOptions(options, currentIndex, 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#navigateOptions(options, currentIndex, -1);
        break;
      case 'Enter':
        event.preventDefault();
        if (currentIndex >= 0) {
          options[currentIndex].click();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.#hidePanel();
        break;
    }
  }

  /**
   * Navigate through options with keyboard
   * @param {HTMLElement[]} options
   * @param {number} currentIndex
   * @param {number} direction
   *
   */
  #navigateOptions(options, currentIndex, direction) {
    if (!options.length) return;

    // Remove active class from current option
    if (currentIndex >= 0) {
      options[currentIndex].classList.remove('autocomplete-option--active');
    }

    // Calculate next index
    let nextIndex = currentIndex + direction;
    if (nextIndex < 0) nextIndex = options.length - 1;
    if (nextIndex >= options.length) nextIndex = 0;

    // Add active class to next option
    options[nextIndex].classList.add('autocomplete-option--active');
    options[nextIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  /**
   * Perform search request
   * @param {string} value
   *
   */
  async #performSearch(value) {
    const searchUrl = this.getAttribute('search-url');
    if (!searchUrl) {
      console.error('Autocompletion: search-url attribute is required');
      return;
    }

    // Build search URL
    let url = searchUrl;
    const searchFormBuilder = this.getAttribute('search-form-builder');
    
    if (searchFormBuilder) {
      try {
        // Execute custom form builder function
        const builderFn = new Function('value', `return ${searchFormBuilder}`);
        url = builderFn(value);
      } catch (error) {
        console.error('Autocompletion: Error executing search-form-builder', error);
        url = searchUrl.replace(/\{\{value\}\}/g, encodeURIComponent(value));
      }
    } else {
      // Replace {{value}} placeholder
      url = searchUrl.replace(/\{\{value\}\}/g, encodeURIComponent(value));
    }

    // Determine search method
    const method = (this.getAttribute('search-method') || 'GET').toUpperCase();

    try {
      // Create new abort controller
      this.abortController = new AbortController();

      // Fetch search results
      const options = {
        method,
        signal: this.abortController.signal,
        headers: {
          'Content-Type': 'application/json',
        }
      };

      // Add body for POST requests
      if (method === 'POST') {
        options.body = JSON.stringify({ query: value });
      }

      const response = await fetch(url, options);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      this.#renderResults(data);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('Autocompletion: Search error', error);
      this.#hidePanel();
    }
  }

  /**
   * Render search results
   * @param {any} data
   *
   */
  #renderResults(data) {
    if (!this.resultPanel) return;

    // Extract data using data-source path
    let results = data;
    if (this.dataSourcePath) {
      const paths = this.dataSourcePath.split('.');
      for (const path of paths) {
        results = results?.[path];
      }
    }

    // Clear previous results
    this.resultPanel.innerHTML = '';

    // Handle empty results
    if (!results || (Array.isArray(results) && results.length === 0)) {
      this.#renderEmptyState();
      return;
    }

    // Ensure results is an array
    if (!Array.isArray(results)) {
      results = [results];
    }

    // Render results using template or default rendering
    if (this.cachedTemplate) {
      results.forEach(item => {
        const rendered = this.#renderTemplate(this.cachedTemplate, item);
        this.resultPanel.insertAdjacentHTML('beforeend', rendered);
      });
    } else {
      results.forEach(item => {
        const option = this.#renderDefaultOption(item);
        this.resultPanel.appendChild(option);
      });
    }

    // Attach click handlers to options
    this.#attachOptionHandlers();

    // Show the panel
    this.#showPanel();
  }

  /**
   * Render template with data
   * @param {string} template
   * @param {Object} data
   * @returns {string}
   *
   */
  #renderTemplate(template, data) {
    return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return data[key] !== undefined ? data[key] : match;
    });
  }

  /**
   * Render default option element
   * @param {any} item
   * @returns {HTMLElement}
   *
   */
  #renderDefaultOption(item) {
    const option = document.createElement('div');
    option.className = 'autocomplete-option';
    
    if (typeof item === 'string') {
      option.textContent = item;
    } else if (typeof item === 'object') {
      option.textContent = item.title || item.name || item.label || JSON.stringify(item);
      if (item.id) {
        option.dataset.value = item.id;
      }
    }
    
    return option;
  }

  /**
   * Render empty state
   *
   */
  #renderEmptyState() {
    if (!this.resultPanel) return;

    this.resultPanel.innerHTML = `
      <div class="autocomplete-empty">
        No results found
      </div>
    `;
    this.#showPanel();
  }

  /**
   * Attach click handlers to option elements
   *
   */
  #attachOptionHandlers() {
    if (!this.resultPanel) return;

    const options = this.resultPanel.querySelectorAll('.autocomplete-option');
    options.forEach(option => {
      option.addEventListener('click', (event) => {
        this.#handleOptionClick(event.currentTarget);
      });
    });
  }

  /**
   * Handle option click
   * @param {HTMLElement} option
   *
   */
  #handleOptionClick(option) {
    // Get the selected value (prefer data-value over text content)
    const value = option.dataset.value || option.textContent;
    const displayText = option.textContent.trim();

    // Dispatch custom event with selected data
    const selectEvent = new CustomEvent('autocomplete:select', {
      detail: { 
        value, 
        displayText,
        element: option 
      },
      bubbles: true
    });
    this.dispatchEvent(selectEvent);

    // Update input values if not prevented
    if (!selectEvent.defaultPrevented) {
      // Update visible input with display text
      if (this.input) {
        this.input.value = displayText;
      }

      // Update hidden input with value (ID)
      if (this.hiddenInput && option.dataset.value) {
        this.hiddenInput.value = option.dataset.value;
      }
    }

    // Hide panel
    this.#hidePanel();
  }

  /**
   * Show result panel
   *
   */
  #showPanel() {
    if (this.resultPanel) {
      this.resultPanel.hidden = false;
    }
  }

  /**
   * Hide result panel
   *
   */
  #hidePanel() {
    if (this.resultPanel) {
      this.resultPanel.hidden = true;
      // Clear active state
      const options = this.resultPanel.querySelectorAll('.autocomplete-option--active');
      options.forEach(opt => opt.classList.remove('autocomplete-option--active'));
    }
  }

  /**
   * Cleanup event listeners and timers
   *
   */
  #cleanup() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.abortController?.abort();
  }
}

// Register the custom element
customElements.define('auto-complete', Autocompletion);

